export class NotificationManager {
	private scene: Phaser.Scene;
	private notifications: string[] = [];
	private isDisplaying: boolean = false;
	private notificationText!: Phaser.GameObjects.Text;
	private notificationBackground!: Phaser.GameObjects.Graphics;
	private startX: number;
	private startY: number;
	private endY: number;
	private borderRadius: number;
	private borderColor: number;
	private borderWidth: number;
	private textColor: string;
	private backgroundColor: number;
	private depthLevel: number;

	constructor(
		scene: Phaser.Scene,
		startX: number = 400,
		startY: number = 650,
		endY: number = 500,
		borderRadius: number = 10,
		borderColor: number = 0xffffff,
		borderWidth: number = 2,
		textColor: string = '#ffffff',
		backgroundColor: number = 0x000000,
		depthLevel: number = 100 // Profundidade
	) {
		this.scene = scene;
		this.startX = startX;
		this.startY = startY;
		this.endY = endY;
		this.borderRadius = borderRadius;
		this.borderColor = borderColor;
		this.borderWidth = borderWidth;
		this.textColor = textColor;
		this.backgroundColor = backgroundColor;
		this.depthLevel = depthLevel;

		// Criar o gráfico para desenhar o fundo e o texto da notificação
		this.notificationBackground = this.scene.add.graphics();
		this.notificationText = this.scene.add
			.text(this.startX, this.startY, '', {
				fontFamily: 'Arial',
				fontSize: '20px',
				color: this.textColor,
				align: 'center'
			})
			.setOrigin(0.5)
			.setDepth(this.depthLevel)
			.setVisible(false);
	}

	// Adiciona uma nova notificação à fila
	public addNotification(message: string): void {
		this.notifications.push(message);
		this.displayNext();
	}

	// Exibe a próxima notificação se houver na fila
	private displayNext(): void {
		if (this.isDisplaying || this.notifications.length === 0) return;

		this.isDisplaying = true;
		const message = this.notifications.shift();

		if (message) {
			// Desenhar o fundo e mostrar o texto
			this.redrawBackground(this.startX, this.startY);
			this.notificationText.setText(message).setPosition(this.startX, this.startY).setVisible(true);

			// Animação de "slide-up" para exibir a notificação
			this.scene.tweens.add({
				targets: this.notificationText,
				y: this.endY,
				duration: 500,
				ease: 'Power2',
				onUpdate: tween => {
					const newY = this.notificationText.y; // Posição atual do texto
					this.redrawBackground(this.startX, newY); // Redesenhar o fundo durante o tween
				},
				onComplete: () => {
					// Esperar 3 segundos antes de esconder a notificação
					this.scene.time.delayedCall(3000, () => {
						this.hideNotification();
					});
				}
			});
		}
	}

	// Oculta a notificação com animação de saída
	private hideNotification(): void {
		this.scene.tweens.add({
			targets: this.notificationText,
			y: this.endY - 100, // Move um pouco para cima antes de desaparecer
			alpha: 0,
			duration: 500,
			ease: 'Power2',
			onUpdate: tween => {
				const newY = this.notificationText.y; // Posição atual do texto
				this.redrawBackground(this.startX, newY); // Redesenhar o fundo durante o tween
			},
			onComplete: () => {
				// Esconde os objetos
				this.notificationText.setVisible(false).setAlpha(1); // Resetar a opacidade para a próxima notificação
				this.notificationBackground.clear(); // Limpar o gráfico para a próxima notificação
				this.isDisplaying = false; // Permitir que outra notificação seja exibida
				this.displayNext(); // Exibe a próxima notificação, se houver
			}
		});
	}

	// Função para redesenhar o fundo da notificação durante a animação
	private redrawBackground(x: number, y: number): void {
		this.notificationBackground.clear();
		this.notificationBackground.fillStyle(this.backgroundColor, 1); // Aplicar cor de fundo
		this.notificationBackground.fillRoundedRect(x - 150, y - 40, 300, 80, this.borderRadius);
		this.notificationBackground.lineStyle(this.borderWidth, this.borderColor, 1);
		this.notificationBackground.strokeRoundedRect(x - 150, y - 40, 300, 80, this.borderRadius);
		this.notificationBackground.setDepth(this.depthLevel); // Garantir que o fundo está na frente
	}
}
